<template>
  <v-card :loading="loading" color="#fffdfb" class="student-exam-seat-map mx-auto">
    <v-toolbar flat color="#fffdfb" class="seat-map-toolbar">
      <v-btn icon color="brown darken-1" :aria-label="$t('student_exam_timetable.back_to_timetable')" @click="goBack">
        <v-icon>mdi-arrow-left</v-icon>
      </v-btn>
      <v-toolbar-title class="brown--text text--darken-2 font-weight-bold">
        {{ $t("student_exam_timetable.seat_map") }}
      </v-toolbar-title>
    </v-toolbar>

    <v-card-text class="pa-4 pa-sm-6">
      <v-alert v-if="error" type="error" dense outlined>
        {{ $t("teach_table.error") }}
      </v-alert>

      <v-alert v-else-if="!loading && !seat" type="info" dense outlined color="deep-orange">
        {{ $t("student_exam_timetable.no_seat") }}
      </v-alert>

      <template v-if="seat">
        <v-card flat outlined color="#fff3ea" class="pa-4 mb-4 exam-summary">
          <v-row dense>
            <v-col cols="12" md="5">
              <v-chip x-small class="white--text font-weight-bold mb-2" :color="examTypeColor">
                {{ examTypeLabel }}
              </v-chip>
              <div class="text-h6 brown--text text--darken-2 font-weight-bold">
                <span>{{ subject.subject_id }} </span>
                <a class="subject-link" @click="openSubjectDialog(subject.subject_id)">
                  {{ subjectName }}
                </a>
              </div>
              <div class="text-body-2 brown--text text--lighten-1 mt-1">
                {{ $t("teach_table.section") }} {{ subject.section }}
              </div>
            </v-col>
            <v-col cols="12" md="7" class="d-flex flex-column align-start align-md-end justify-center">
              <div class="d-flex flex-wrap justify-md-end">
                <v-chip small :ripple="false" color="amber lighten-5" text-color="deep-orange darken-2" class="static-chip ma-1">
                  <v-icon left small>mdi-calendar-range</v-icon>
                  {{ $t("student_exam_timetable.year_semester") }} {{ selectedTermLabel }}
                </v-chip>
                <v-chip small :ripple="false" color="light-blue lighten-5" text-color="blue-grey darken-2" class="static-chip ma-1">
                  <v-icon left small>mdi-account-badge-outline</v-icon>
                  {{ $t("student_exam_timetable.student_id") }} {{ studentId }}
                </v-chip>
              </div>
              <div class="d-flex flex-wrap justify-md-end">
                <v-chip small :ripple="false" color="white" text-color="brown darken-2" class="static-chip ma-1">
                  <v-icon left small>mdi-office-building-outline</v-icon>
                  {{ $t("student_exam_timetable.building") }} {{ seat.building_name || "-" }}
                </v-chip>
                <v-chip small :ripple="false" color="white" text-color="brown darken-2" class="static-chip ma-1">
                  <v-icon left small>mdi-door</v-icon>
                  {{ $t("student_exam_timetable.room") }} {{ seat.room_name || "-" }}
                </v-chip>
                <v-chip small :ripple="false" color="white" text-color="deep-orange darken-2" class="static-chip ma-1">
                  <v-icon left small>mdi-seat</v-icon>
                  {{ $t("student_exam_timetable.seat") }} {{ assignedSeatLabel }}
                </v-chip>
              </div>
            </v-col>
          </v-row>
        </v-card>

        <v-card flat outlined color="white" class="seat-layout pa-3 pa-sm-5">
          <div class="d-flex d-sm-none align-center justify-center mb-2 text-caption brown--text text--lighten-1">
            <v-icon small color="brown" class="mr-1">mdi-gesture-swipe-left</v-icon>
            {{ $t("student_exam_timetable.scroll_left_hint") }}
          </div>
          <div class="seat-map-scroll">
            <div class="seat-map-content" :style="seatMapStyle">
              <v-sheet
                rounded
                color="orange lighten-4"
                class="room-front py-2 px-4 mb-5 text-center brown--text text--darken-2 font-weight-bold"
              >
                <v-icon left color="brown">mdi-presentation</v-icon>
                {{ $t("student_exam_timetable.room_front") }}
              </v-sheet>

              <div v-if="hasLayout" class="seat-grid" :style="seatGridStyle">
                <v-card
                  v-for="roomSeat in roomSeats"
                  :key="roomSeat.label"
                  :outlined="!roomSeat.assigned"
                  color="amber lighten-5"
                  width="56"
                  height="56"
                  class="seat-card d-flex align-center justify-center"
                  :class="{ 'seat-card--assigned': roomSeat.assigned }"
                >
                  <v-card-text class="pa-1 d-flex align-center justify-center font-weight-bold brown--text text--darken-2">
                    <v-icon v-if="roomSeat.assigned" size="22" color="deep-orange" class="seat-person-icon">mdi-account</v-icon>
                    <span>{{ roomSeat.label }}</span>
                  </v-card-text>
                </v-card>
              </div>

              <v-alert v-else type="warning" dense outlined class="mb-0">
                {{ $t("student_exam_timetable.no_seat") }}
              </v-alert>
            </div>
          </div>

        </v-card>
      </template>
      <subject-dialog ref="subject_dialog" :level-id="levelId" />
    </v-card-text>
  </v-card>
</template>

<script>
import RegistrationProvider from "@/services/registration.provider.js";
import SubjectDialog from "@/views/teach_table/components/SubjectDialog.vue";

const RegistrationService = new RegistrationProvider();

export default {
  name: "StudentExamSeatMap",
  components: {
    SubjectDialog,
  },
  data: () => ({
    loading: false,
    error: false,
    subject: null,
    seat: null,
  }),
  computed: {
    studentId() {
      return this.$route.query.student_id || this.$store.state.user.surrogated_student_id;
    },
    year() {
      return this.$route.query.year;
    },
    semester() {
      return this.$route.query.semester;
    },
    levelId() {
      return this.$route.query.level_id || "1";
    },
    examPeriod() {
      return this.$route.query.exam_period === "M" ? "M" : "F";
    },
    examTypeLabel() {
      return this.examPeriod === "M" ? "Midterm" : "Final";
    },
    examTypeColor() {
      return this.examPeriod === "M" ? "indigo" : "deep-orange";
    },
    subjectName() {
      if (!this.subject) return "";
      return this.subject.subject_ename || this.subject.subject_name_en || "-";
    },
    selectedTermLabel() {
      if (!this.year || !this.semester) return "-";
      const year = this.$i18n.locale === "en" && Number(this.year) > 2400
        ? Number(this.year) - 543
        : this.year;
      return `${this.semester} / ${year}`;
    },
    assignedSeatLabel() {
      return this.seat ? `${this.columnLabel(this.seat.col)}${this.seat.row}` : "-";
    },
    rowCount() {
      return this.seat ? Number(this.seat.room_row_count) || 0 : 0;
    },
    columnCount() {
      return this.seat ? Number(this.seat.room_col_count) || 0 : 0;
    },
    hasLayout() {
      return this.rowCount > 0 && this.columnCount > 0;
    },
    roomSeats() {
      const seats = [];
      for (let row = 1; row <= this.rowCount; row += 1) {
        for (let column = 1; column <= this.columnCount; column += 1) {
          seats.push({
            row,
            column,
            label: `${this.columnLabel(column)}${row}`,
            assigned: Number(this.seat.row) === row && Number(this.seat.col) === column,
          });
        }
      }
      return seats;
    },
    seatGridStyle() {
      return {
        gridTemplateColumns: `repeat(${this.columnCount}, 56px)`,
      };
    },
    seatMapStyle() {
      const width = this.hasLayout
        ? (this.columnCount * 56) + ((this.columnCount - 1) * 12)
        : 320;
      return {
        width: `${width}px`,
        minWidth: `${width}px`,
      };
    },
  },
  methods: {
    columnLabel(columnNumber) {
      let number = Number(columnNumber);
      let label = "";
      while (number > 0) {
        number -= 1;
        label = String.fromCharCode(65 + (number % 26)) + label;
        number = Math.floor(number / 26);
      }
      return label;
    },
    openSubjectDialog(subjectId) {
      if (this.$refs.subject_dialog) {
        this.$refs.subject_dialog.show_item_detail(subjectId);
      }
    },
    goBack() {
      this.$router.push({
        name: "student_exam_timetable",
        query: {
          student_id: this.studentId,
          year: this.year,
          semester: this.semester,
          level_id: this.levelId,
        },
      });
    },
    async fetchData() {
      if (!this.studentId || !this.year || !this.semester || !this.$route.query.subject_id) {
        this.error = true;
        return;
      }

      try {
        this.loading = true;
        this.error = false;
        const data = await RegistrationService.getRegisResult({
          student_id: this.studentId,
          year: this.year,
          semester: this.semester,
          level_id: this.levelId,
        });
        this.subject = data.find(
          (item) => String(item.subject_id) === String(this.$route.query.subject_id)
        ) || null;
        this.seat = this.subject && this.subject.exam_seats
          ? this.subject.exam_seats[this.examPeriod] || null
          : null;
      } catch (error) {
        this.error = true;
        this.subject = null;
        this.seat = null;
      } finally {
        this.loading = false;
      }
    },
  },
  created() {
    this.fetchData();
  },
};
</script>

<style scoped>
.student-exam-seat-map {
  max-width: 1440px;
  background: #fff3ea;
}

.seat-map-toolbar {
  background: #fff3ea !important;
}

.seat-map-toolbar,
.exam-summary,
.seat-layout {
  border-color: #f1e1d6 !important;
}

.exam-summary {
  border-radius: 12px !important;
}

.seat-layout {
  border-radius: 12px !important;
  width: 100%;
  min-width: 0;
  max-width: 100%;
  overflow: hidden;
}

.subject-link {
  color: #1976d2;
  cursor: pointer;
  text-decoration: none;
  font-weight: inherit;
}

.subject-link:hover {
  text-decoration: none;
}

.static-chip::before {
  opacity: 0 !important;
}

.seat-map-scroll {
  width: 100%;
  min-width: 0;
  max-width: 100%;
  overflow-x: auto;
  overflow-y: hidden;
  overscroll-behavior-inline: contain;
}

.seat-map-content {
  box-sizing: content-box;
  margin: 0 auto;
  padding: 18px 28px;
}

.room-front {
  width: 90%;
  margin-right: auto;
  margin-left: auto;
}

.seat-grid {
  display: grid;
  gap: 12px;
  justify-content: center;
}

.seat-card {
  flex: 0 0 56px;
  box-sizing: border-box;
  border-color: #efcf94 !important;
  transition: none;
}

.seat-person-icon {
  margin-right: 2px;
}

.seat-card.seat-card--assigned {
  z-index: 1;
  outline: none;
  border: none !important;
  box-shadow:
    inset 0 0 0 1px rgba(122, 95, 81, 0.08),
    0 0 0 2px #ff7043,
    0 0 18px rgba(255, 112, 67, 0.76) !important;
}
</style>
